'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MousePointerClick, Megaphone, Link2, Users, Calendar, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';

interface LinkClick {
  id: string;
  trackable_link_id: string;
  campaign_id: string;
  campaign_name: string;
  lead_id: string | null;
  lead_name: string | null;
  lead_phone: string | null;
  clicked_at: string;
}

interface TrackableLink {
  id: string;
  campaign_id: string;
  campaign_name: string;
  label: string;
  short_code: string;
  original_url: string;
}

interface CampaignStat {
  campaign_id: string;
  campaign_name: string;
  total_clicks: number;
  unique_leads: number;
  links: LinkStat[];
}

interface LinkStat {
  link_id: string;
  label: string;
  short_code: string;
  original_url: string;
  total_clicks: number;
  clicks: LinkClick[];
}

type GroupBy = 'campaign' | 'link' | 'lead';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

export default function LinkClickReportsPage() {
  const supabase = useMemo(() => createClient(), []);
  const [clicks, setClicks] = useState<LinkClick[]>([]);
  const [links, setLinks] = useState<TrackableLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('campaign');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [filterCampaign, setFilterCampaign] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        supabase.from('link_clicks').select('*').order('clicked_at', { ascending: false }).limit(5000),
        supabase.from('trackable_links').select('*').order('created_at', { ascending: false }).limit(5000),
      ]);
      const clickErr = results[0].error;
      const linkErr = results[1].error;
      const clickData = results[0].data;
      const linkData = results[1].data;
      if (clickErr) {
        console.error('[LinkClickReports] link_clicks fetch error:', clickErr);
        throw clickErr;
      }
      if (linkErr) {
        console.error('[LinkClickReports] trackable_links fetch error:', linkErr);
        throw linkErr;
      }
      setClicks(clickData ?? []);
      setLinks(linkData ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : (err as { message?: string })?.message ?? 'Failed to load click data';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Build campaign stats
  const campaignStats: CampaignStat[] = (() => {
    const map: Record<string, CampaignStat> = {};
    const filteredClicks = filterCampaign
      ? clicks.filter((c) => c.campaign_id === filterCampaign)
      : clicks;

    filteredClicks.forEach((click) => {
      if (!map[click.campaign_id]) {
        map[click.campaign_id] = {
          campaign_id: click.campaign_id,
          campaign_name: click.campaign_name,
          total_clicks: 0,
          unique_leads: 0,
          links: [],
        };
      }
      const camp = map[click.campaign_id];
      camp.total_clicks++;

      // Link stats
      const link = links.find((l) => l.id === click.trackable_link_id);
      if (link) {
        let linkStat = camp.links.find((ls) => ls.link_id === link.id);
        if (!linkStat) {
          linkStat = { link_id: link.id, label: link.label, short_code: link.short_code, original_url: link.original_url, total_clicks: 0, clicks: [] };
          camp.links.push(linkStat);
        }
        linkStat.total_clicks++;
        linkStat.clicks.push(click);
      }
    });

    // Count unique leads per campaign
    Object.values(map).forEach((camp) => {
      const leadSet = new Set(filteredClicks.filter((c) => c.campaign_id === camp.campaign_id && c.lead_id).map((c) => c.lead_id));
      camp.unique_leads = leadSet.size;
    });

    return Object.values(map).sort((a, b) => b.total_clicks - a.total_clicks);
  })();

  // Build per-link stats (flat)
  const linkStats: LinkStat[] = (() => {
    const map: Record<string, LinkStat> = {};
    const filteredClicks = filterCampaign
      ? clicks.filter((c) => c.campaign_id === filterCampaign)
      : clicks;

    filteredClicks.forEach((click) => {
      const link = links.find((l) => l.id === click.trackable_link_id);
      if (!link) return;
      if (!map[link.id]) {
        map[link.id] = { link_id: link.id, label: link.label, short_code: link.short_code, original_url: link.original_url, total_clicks: 0, clicks: [] };
      }
      map[link.id].total_clicks++;
      map[link.id].clicks.push(click);
    });
    return Object.values(map).sort((a, b) => b.total_clicks - a.total_clicks);
  })();

  // Build per-lead stats
  const leadStats: { lead_id: string; lead_name: string; lead_phone: string; total_clicks: number; clicks: LinkClick[] }[] = (() => {
    const map: Record<string, { lead_id: string; lead_name: string; lead_phone: string; total_clicks: number; clicks: LinkClick[] }> = {};
    const filteredClicks = filterCampaign
      ? clicks.filter((c) => c.campaign_id === filterCampaign)
      : clicks;

    filteredClicks.forEach((click) => {
      const key = click.lead_id ?? `anon-${click.id}`;
      if (!map[key]) {
        map[key] = {
          lead_id: click.lead_id ?? 'Unknown',
          lead_name: click.lead_name ?? 'Unknown Lead',
          lead_phone: click.lead_phone ?? '—',
          total_clicks: 0,
          clicks: [],
        };
      }
      map[key].total_clicks++;
      map[key].clicks.push(click);
    });
    return Object.values(map).sort((a, b) => b.total_clicks - a.total_clicks);
  })();

  const uniqueCampaigns = Array.from(new Map(clicks.map((c) => [c.campaign_id, c.campaign_name])).entries());
  const totalClicks = (filterCampaign ? clicks.filter((c) => c.campaign_id === filterCampaign) : clicks).length;
  const totalLinks = linkStats.length;
  const totalLeads = leadStats.filter((l) => l.lead_id !== 'Unknown').length;

  return (
    <AppLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border bg-background shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <MousePointerClick size={18} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Link Click Reports</h1>
                <p className="text-xs text-muted-foreground">Track clicks per campaign, link, and lead</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Clicks', value: totalClicks, icon: MousePointerClick, color: 'text-primary bg-primary/10' },
              { label: 'Links Tracked', value: totalLinks, icon: Link2, color: 'text-violet-600 bg-violet-50' },
              { label: 'Leads Tracked', value: totalLeads, icon: Users, color: 'text-emerald-600 bg-emerald-50' },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${kpi.color}`}>
                  <kpi.icon size={18} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                  <p className="text-xs text-muted-foreground">{kpi.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters & Group By */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
              {(['campaign', 'link', 'lead'] as GroupBy[]).map((g) => (
                <button
                  key={g}
                  onClick={() => setGroupBy(g)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors capitalize ${groupBy === g ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  By {g}
                </button>
              ))}
            </div>

            <select
              value={filterCampaign}
              onChange={(e) => setFilterCampaign(e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All Campaigns</option>
              {uniqueCampaigns.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : totalClicks === 0 ? (
            <div className="bg-card border border-border rounded-xl py-16 text-center">
              <MousePointerClick size={40} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No click data yet</p>
              <p className="text-xs text-muted-foreground mt-1">Generate trackable links in Campaign Management and share them with leads</p>
            </div>
          ) : (
            <>
              {/* By Campaign */}
              {groupBy === 'campaign' && (
                <div className="space-y-3">
                  {campaignStats.map((camp) => (
                    <div key={camp.campaign_id} className="bg-card border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleExpand(camp.campaign_id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/20 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Megaphone size={14} className="text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold text-foreground">{camp.campaign_name}</p>
                            <p className="text-xs text-muted-foreground">{camp.links.length} link{camp.links.length !== 1 ? 's' : ''} · {camp.unique_leads} lead{camp.unique_leads !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-bold text-foreground">{camp.total_clicks}</p>
                            <p className="text-xs text-muted-foreground">total clicks</p>
                          </div>
                          {expandedIds.has(camp.campaign_id) ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                        </div>
                      </button>

                      {expandedIds.has(camp.campaign_id) && (
                        <div className="border-t border-border">
                          {camp.links.map((ls) => (
                            <div key={ls.link_id} className="border-b border-border last:border-0">
                              <button
                                onClick={() => toggleExpand(`${camp.campaign_id}-${ls.link_id}`)}
                                className="w-full flex items-center justify-between px-5 py-3 bg-muted/20 hover:bg-muted/40 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <Link2 size={13} className="text-muted-foreground" />
                                  <span className="text-sm font-medium text-foreground">{ls.label}</span>
                                  <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">{ls.original_url}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold text-foreground">{ls.total_clicks} clicks</span>
                                  {expandedIds.has(`${camp.campaign_id}-${ls.link_id}`) ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
                                </div>
                              </button>

                              {expandedIds.has(`${camp.campaign_id}-${ls.link_id}`) && (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-muted/30 border-b border-border">
                                        <th className="text-left px-5 py-2 font-semibold text-muted-foreground">Lead</th>
                                        <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Phone</th>
                                        <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Clicked At</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                      {ls.clicks.map((click) => (
                                        <tr key={click.id} className="hover:bg-muted/10">
                                          <td className="px-5 py-2.5 font-medium text-foreground">{click.lead_name ?? 'Unknown'}</td>
                                          <td className="px-4 py-2.5 text-muted-foreground">{click.lead_phone ?? '—'}</td>
                                          <td className="px-4 py-2.5 text-muted-foreground flex items-center gap-1.5">
                                            <Calendar size={11} />
                                            {formatDate(click.clicked_at)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* By Link */}
              {groupBy === 'link' && (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground">Link Label</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Campaign</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Destination</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Clicks</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {linkStats.map((ls) => {
                        const linkObj = links.find((l) => l.id === ls.link_id);
                        return (
                          <React.Fragment key={ls.link_id}>
                            <tr className="hover:bg-muted/20 transition-colors">
                              <td className="px-5 py-3 font-medium text-foreground">{ls.label}</td>
                              <td className="px-4 py-3">
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                                  {linkObj ? clicks.find((c) => c.trackable_link_id === ls.link_id)?.campaign_name ?? '—' : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[200px]">{ls.original_url}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                                  <MousePointerClick size={12} className="text-primary" />
                                  {ls.total_clicks}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => toggleExpand(ls.link_id)}
                                  className="text-xs text-primary hover:underline flex items-center gap-1 ml-auto"
                                >
                                  {expandedIds.has(ls.link_id) ? 'Hide' : 'View'} leads
                                  {expandedIds.has(ls.link_id) ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </button>
                              </td>
                            </tr>
                            {expandedIds.has(ls.link_id) && (
                              <tr>
                                <td colSpan={5} className="px-0 py-0 bg-muted/10">
                                  <table className="w-full text-xs border-t border-border">
                                    <thead>
                                      <tr className="bg-muted/30">
                                        <th className="text-left px-8 py-2 font-semibold text-muted-foreground">Lead</th>
                                        <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Phone</th>
                                        <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Clicked At</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                      {ls.clicks.map((click) => (
                                        <tr key={click.id} className="hover:bg-muted/10">
                                          <td className="px-8 py-2 font-medium text-foreground">{click.lead_name ?? 'Unknown'}</td>
                                          <td className="px-4 py-2 text-muted-foreground">{click.lead_phone ?? '—'}</td>
                                          <td className="px-4 py-2 text-muted-foreground">{formatDate(click.clicked_at)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* By Lead */}
              {groupBy === 'lead' && (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground">Lead</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Phone</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Total Clicks</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {leadStats.map((lead) => (
                        <React.Fragment key={lead.lead_id}>
                          <tr className="hover:bg-muted/20 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                                  {(lead.lead_name ?? 'U')[0].toUpperCase()}
                                </div>
                                <span className="font-medium text-foreground">{lead.lead_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{lead.lead_phone}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                                <MousePointerClick size={12} className="text-primary" />
                                {lead.total_clicks}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => toggleExpand(lead.lead_id)}
                                className="text-xs text-primary hover:underline flex items-center gap-1 ml-auto"
                              >
                                {expandedIds.has(lead.lead_id) ? 'Hide' : 'View'} clicks
                                {expandedIds.has(lead.lead_id) ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              </button>
                            </td>
                          </tr>
                          {expandedIds.has(lead.lead_id) && (
                            <tr>
                              <td colSpan={4} className="px-0 py-0 bg-muted/10">
                                <table className="w-full text-xs border-t border-border">
                                  <thead>
                                    <tr className="bg-muted/30">
                                      <th className="text-left px-8 py-2 font-semibold text-muted-foreground">Campaign</th>
                                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Link</th>
                                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Clicked At</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border">
                                    {lead.clicks.map((click) => {
                                      const linkObj = links.find((l) => l.id === click.trackable_link_id);
                                      return (
                                        <tr key={click.id} className="hover:bg-muted/10">
                                          <td className="px-8 py-2">
                                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{click.campaign_name}</span>
                                          </td>
                                          <td className="px-4 py-2 font-medium text-foreground">{linkObj?.label ?? '—'}</td>
                                          <td className="px-4 py-2 text-muted-foreground">{formatDate(click.clicked_at)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
